/**
 * Intervals field formatter
 * Handles composite field (spacing/flowRate + timeOnJobSite)
 */

import {useLoadSpacing} from '@hconnect/tuc-processes/fields'
import {useChangeableField} from '@hconnect/tuc-processes/orderManagement/orderChange'

import {getCombinedFieldButtonStateProps} from '../../utils/getFieldButtonStateProps'

import type {FieldFormatter, FieldFormatterResult} from './types'

export const useIntervalsFormatter = (): FieldFormatter => {
  const {spacingOrFlowRate, timeOnJobSite, defaultSpacingOrFlowRate, defaultTimeOnJobSite} =
    useLoadSpacing()
  const intervalsStates = useChangeableField(['spacingOrFlowRate', 'timeOnJobSite'])

  return (context) => {
    const {t} = context

    // Composite field - approval is evaluated per field, so an untouched field that is past
    // its own lead-time deadline does not flag an approval for the field the user changed.
    const {isChanged: hasChanges, requiresApproval} =
      getCombinedFieldButtonStateProps(intervalsStates)

    // Format intervals display string
    const formatIntervals = (spacing: unknown, timeOnSite: unknown): string | null => {
      const parts: string[] = []

      if (spacing && typeof spacing === 'object') {
        const spacingObj = spacing as {
          type?: string
          loadSpace?: number
          flowRate?: number
          loadSpaceUom?: string
          flowRateUom?: string
        }
        const isLoadSpace = spacingObj.type === 'loadSpace'
        const typeLabel = isLoadSpace
          ? t('orderIntake.rmc.orderManagement.spacing', 'Spacing')
          : t('orderIntake.rmc.orderManagement.flowRate', 'Flow rate')
        const value = isLoadSpace ? spacingObj.loadSpace : spacingObj.flowRate
        const unit = isLoadSpace ? spacingObj.loadSpaceUom : spacingObj.flowRateUom

        if (value !== null && value !== undefined && unit) {
          parts.push(`${typeLabel} - ${value} ${unit}`)
        }
      }

      if (timeOnSite && typeof timeOnSite === 'object') {
        const timeOnSiteObj = timeOnSite as {time?: number; timeUom?: string}
        if (timeOnSiteObj.time && timeOnSiteObj.timeUom) {
          parts.push(
            `${t('orderIntake.rmc.orderManagement.timeOnSite', 'Time on site')} - ${
              timeOnSiteObj.time
            } ${timeOnSiteObj.timeUom}`
          )
        }
      }

      return parts.length > 0 ? parts.join(', ') : null
    }

    const oldValue = formatIntervals(defaultSpacingOrFlowRate, defaultTimeOnJobSite)
    const newValue = formatIntervals(spacingOrFlowRate, timeOnJobSite)

    const values: FieldFormatterResult['values'] = []

    if (oldValue || newValue) {
      if (oldValue && hasChanges) {
        values.push({text: oldValue, isDiscarded: true, isNew: false})
      }
      if (newValue) {
        values.push({text: newValue, isDiscarded: false, isNew: hasChanges})
      }
    } else {
      values.push({
        text: t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified'),
        isDiscarded: false,
        isNew: false
      })
    }

    return {values, requiresApproval, hasChanges}
  }
}
